import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import dayjs from "utils/dayjs";
import { DataTable } from "@outpave/ds";
import type { DataColumn } from "@outpave/ds";
import { GET_COMPANIES } from "../../../graphql/company";
import useInfiniteScroll from "hooks/useInfiniteScroll";
import { CompanyFilterCriteriaProps } from "pages/platform/Accounts";
import { DEFAULT_PAGINATION_SIZE } from "components/common/Constants";
import CompanyFilterType from "enums/CompanyFilterType";
import StatusType from "enums/StatusType";
import { useNavigate } from "react-router-dom";
import { RenderAccountStatus } from "components/common/RenderAccountStatus";
import { accountHolderPath } from "utils/platformAccounts/accountPaths";

type Account = {
  id: string;
  businessLegalName: string;
  numberOfUser: number;
  createdAt: string;
  status: StatusType;
  isDeactivateRequested: boolean;
};

type CompaniesData = Record<
  string,
  { edges: { node: Omit<Account, "createdAt"> & { createdAt: number } }[] }
>;

type AccountTableProps = {
  filterCriteria: CompanyFilterCriteriaProps;
  refreshAccounts?: number;
};

const AccountsTable: React.FC<AccountTableProps> = (props) => {
  const { filterCriteria, refreshAccounts } = props;
  const navigate = useNavigate();

  const { t } = useTranslation();

  const [defaultPaginationVariables, setDefaultPaginationVariables] = useState(
    {},
  );

  useEffect(() => {
    if (filterCriteria && Object.keys(filterCriteria).length > 0) {
      setDefaultPaginationVariables({
        first: DEFAULT_PAGINATION_SIZE,
        after: undefined,
        status:
          filterCriteria.companyFilterType === CompanyFilterType.ShowAll
            ? undefined
            : filterCriteria.companyFilterType,
        searchTerm: filterCriteria.searchTerm,
      });
    }
  }, [filterCriteria]);

  const extractAccounts = (data: CompaniesData): Account[] => {
    const firstKey = Object.keys(data)[0];
    return (data[firstKey]?.edges ?? []).map((item) => ({
      id: item.node.id,
      businessLegalName: item.node.businessLegalName,
      status: item.node.status,
      numberOfUser: item.node.numberOfUser,
      createdAt: dayjs(item.node.createdAt).format("YYYY/MM/DD"),
      isDeactivateRequested: item.node.isDeactivateRequested,
    }));
  };

  const { dataArray, displayLoading, handleScroll, isInfiniteScrollFetching } =
    useInfiniteScroll({
      query: GET_COMPANIES,
      initialVariables: defaultPaginationVariables,
      extractData: extractAccounts,
      refetchIndex: refreshAccounts,
    });

  const columns: DataColumn<Account>[] = useMemo(
    () => [
      {
        id: "businessLegalName",
        header: t("Account"),
        cell: (account) => account.businessLegalName,
      },
      {
        id: "numberOfUser",
        header: t("# of Users"),
        align: "center",
        cell: (account) => account.numberOfUser,
      },
      {
        id: "createdAt",
        header: t("Created"),
        cell: (account) => account.createdAt,
      },
      {
        id: "status",
        header: t("Status"),
        cell: (account) => (
          <RenderAccountStatus
            accountStatus={account.status}
            isDeactivateRequested={account.isDeactivateRequested}
          />
        ),
      },
    ],
    [t],
  );

  return (
    <DataTable<Account>
      columns={columns}
      rows={dataArray}
      getRowKey={(account) => account.id}
      loading={displayLoading}
      isFetchingMore={isInfiniteScrollFetching}
      onRowClick={(account) => navigate(accountHolderPath(account.id))}
      onScroll={handleScroll}
      emptyState={t("platform.noAccountsFound")}
      loadingMoreText={t("LoadingMore")}
      stickyHeader
    />
  );
};

export default AccountsTable;
